/**
 * Slug generation for the browser — SUGGESTIONS ONLY.
 *
 * `slugifyBase` mirrors `server/src/lib/slug.ts` character for character, so what a user sees
 * previewed matches what they get in the common case. It deliberately does NOT handle
 * collisions: the server owns uniqueness, re-resolves every slug it is sent, and may hand
 * back `overview-2` where this showed `overview`. Treat anything from here as a placeholder
 * until the create response comes back.
 *
 * The two copies must stay in step. If you change one, change the other.
 */

/** Longest slug generated, before any disambiguating suffix. Matches the server. */
const MAX_LENGTH = 60;

/**
 * Text -> slug.
 *
 * `fallbackPrefix` is required rather than defaulted. The old browser implementation
 * hardcoded `'doc-' + Date.now()`, which is wrong for anything that is not a doc — callers
 * say what they are naming.
 */
export function slugifyBase(text: string | null | undefined, fallbackPrefix: string): string {
  const slug = (text || '')
    .toLowerCase()
    .normalize('NFKD')
    // strip combining diacritics (U+0300–U+036F) left behind by the decomposition, so
    // "Café" slugs to `cafe` rather than `caf` — the app is multilingual
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_LENGTH)
    // A trailing hyphen can reappear after the slice; strip it again so "foo-" is never a slug.
    .replace(/-+$/g, '');
  return slug || fallbackPrefix;
}

export const suggestDocSlug = (title: string | null | undefined): string =>
  slugifyBase(title, `doc-${Date.now()}`);

export const suggestNodeSlug = (label: string | null | undefined): string =>
  slugifyBase(label, `node-${Date.now()}`);
